// =============================================================================
// Base API Client
// =============================================================================

const API_BASE_URL = (() => {
  const raw = import.meta.env.VITE_API_BASE_URL
  if (!raw) {
    throw new Error('VITE_API_BASE_URL environment variable is required')
  }
  return raw.endsWith('/') ? raw : `${raw}/`
})()

const API_TIMEOUT = 30000

// Build URL with optional query params
const buildUrl = (endpoint: string, params?: Record<string, any>): URL => {
  const url = new URL(endpoint.replace(/^\//, ''), API_BASE_URL)
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        // Handle arrays by joining with commas
        url.searchParams.append(key, Array.isArray(value) ? value.join(',') : String(value))
      }
    })
  }
  return url
}

// Get JWT token from Amplify
const getAuthToken = async (): Promise<string | null> => {
  try {
    const { fetchAuthSession } = await import('aws-amplify/auth')
    const session = await fetchAuthSession()
    return session.tokens?.idToken?.toString() || null
  } catch (error) {
    console.error('Failed to get auth token:', error)
    return null
  }
}

// Extract error message from FastAPI response
const extractErrorMessage = (errorData: any, fallback: string): string => {
  if (!errorData?.detail) return fallback
  if (typeof errorData.detail === 'string') return errorData.detail
  if (Array.isArray(errorData.detail)) {
    return errorData.detail.map((e: any) => e.msg || JSON.stringify(e)).join(', ')
  }
  return fallback
}

// Core fetch wrapper with auth, timeout, and error handling
export const request = async <T = any>(
  endpoint: string,
  options: {
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
    params?: Record<string, any>
    body?: any
    timeout?: number
    isFormData?: boolean
  } = {}
): Promise<T> => {
  const { method = 'GET', params, body, timeout = API_TIMEOUT, isFormData = false } = options
  const url = buildUrl(endpoint, params)

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeout)

  try {
    const token = await getAuthToken()

    const headers: Record<string, string> = {}
    if (token) headers['Authorization'] = `Bearer ${token}`
    if (!isFormData) headers['Content-Type'] = 'application/json'

    const response = await fetch(url.toString(), {
      method,
      headers,
      body: isFormData ? body : body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    })

    clearTimeout(timeoutId)

    if (!response.ok) {
      if (response.status === 401) throw new Error('Unauthorized access')
      if (response.status >= 500) throw new Error('Server error')

      const errorData = await response.json().catch(() => ({}))
      throw new Error(extractErrorMessage(errorData, `HTTP error! status: ${response.status}`))
    }

    if (response.status === 204) {
      return undefined as T
    }

    const contentType = response.headers.get('content-type') || ''
    if (!contentType.includes('application/json')) {
      return undefined as T
    }

    return await response.json()
  } catch (error: any) {
    clearTimeout(timeoutId)
    if (error.name === 'AbortError') throw new Error('Request timeout')
    throw error
  }
}

// =============================================================================
// Public API Methods
// =============================================================================

export const api = {
  get: <T = any>(endpoint: string, params?: Record<string, any>) =>
    request<T>(endpoint, { method: 'GET', params }),

  post: <T = any>(endpoint: string, body?: any) =>
    request<T>(endpoint, { method: 'POST', body }),

  put: <T = any>(endpoint: string, body?: any) =>
    request<T>(endpoint, { method: 'PUT', body }),

  patch: <T = any>(endpoint: string, body?: any) =>
    request<T>(endpoint, { method: 'PATCH', body }),

  delete: <T = any>(endpoint: string) =>
    request<T>(endpoint, { method: 'DELETE' }),

  upload: <T = any>(endpoint: string, formData: FormData) =>
    request<T>(endpoint, { method: 'POST', body: formData, isFormData: true }),
}

// =============================================================================
// Matches - Complex param building kept as wrapper
// =============================================================================

export const fetchMatches = (params?: {
  start_date?: string
  end_date?: string
  player_ids?: string[]
  competition_ids?: string[]
  categories?: string[]
  publishing_statuses?: string[]
  venue_ids?: string[]
  search?: string
  sort_order?: 'asc' | 'desc'
  page?: number
  limit?: number
}) => {
  const apiParams: Record<string, any> = {}
  if (params?.start_date) apiParams.start_date = params.start_date
  if (params?.end_date) apiParams.end_date = params.end_date
  if (params?.player_ids?.length) apiParams.player_ids = params.player_ids
  if (params?.competition_ids?.length) apiParams.competition_ids = params.competition_ids
  if (params?.categories?.length) apiParams.categories = params.categories
  if (params?.publishing_statuses?.length) apiParams.publishing_statuses = params.publishing_statuses
  if (params?.venue_ids?.length) apiParams.venue_ids = params.venue_ids
  if (params?.search?.trim()) apiParams.search = params.search.trim()
  if (params?.sort_order) apiParams.sort_order = params.sort_order
  if (params?.page) apiParams.page = params.page
  if (params?.limit) apiParams.limit = params.limit

  return api.get('/matches', apiParams)
}

export const updateMatch = (matchId: string, data: any) =>
  api.put(`/matches/${matchId}`, data)

export const createMatch = (data: Record<string, any>) =>
  api.post('/matches', data)

// =============================================================================
// Feed Management
// =============================================================================

export const updateVenueFeedMappings = (mappings: Array<{ feed_id: string; venue_id: string; season_id: string }>) =>
  api.put('/publishing/venue-feed-mappings', { mappings })

// =============================================================================
// Image Management
// =============================================================================

type EntityType = 'match' | 'player' | 'tournament' | 'competitionLevel' | 'vod'

const getEntityEndpoint = (entityType: EntityType, entityId: string): string => {
  const entityMap = {
    match: `matches/${entityId}`,
    player: `players/${entityId}`,
    competitionLevel: `competition-levels/${entityId}`,
    tournament: `competitions/tournaments/${entityId}`,
    vod: `vod/assets/${entityId}`,
  }
  return entityMap[entityType]
}

export const uploadImage = (
  entityType: EntityType,
  entityId: string,
  file: File,
  metadata?: { title?: string; caption?: string; alt_text?: string; image_type?: string }
) => {
  const formData = new FormData()
  formData.append('file', file)
  if (metadata?.title) formData.append('title', metadata.title)
  if (metadata?.caption) formData.append('caption', metadata.caption)
  if (metadata?.alt_text) formData.append('alt_text', metadata.alt_text)
  if (metadata?.image_type) formData.append('image_type', metadata.image_type)

  return api.upload<{ url: string }>(`/${getEntityEndpoint(entityType, entityId)}/images`, formData)
}

export const fetchImages = (entityType: EntityType, entityId: string) =>
  api.get(`/${getEntityEndpoint(entityType, entityId)}/images`)

export const generateMatchcard = (matchId: string) =>
  api.post(`/matches/${matchId}/generate-matchcard`)

// =============================================================================
// Players
// =============================================================================

export const createPlayer = (data: {
  tc_name: string
  tc_short_name?: string | null
  tc_abbreviation?: string | null
  tc_country_code?: string | null
}) =>
  api.post('/players', data)

export const updatePlayer = (playerId: string, data: {
  tc_name?: string | null
  tc_short_name?: string | null
  tc_abbreviation?: string | null
  tc_country_code?: string | null
}) =>
  api.patch(`/players/${playerId}`, data)

export const deleteImage = (entityType: EntityType, entityId: string, imageId: string, reason?: string) =>
  request(`/${getEntityEndpoint(entityType, entityId)}/images`, {
    method: 'DELETE',
    params: { image_id: imageId },
    body: reason ? { reason } : undefined,
  })

export const fetchCompetitionLevels = () =>
  api.get('/competition-levels')

// =============================================================================
// Competitions & Seasons
// =============================================================================

export interface TournamentSettingsChild {
  tc_competition_id?: string  // If present, update existing; if absent, create new
  tc_name?: string
  short_name?: string
  tc_type?: string
  tc_gender?: string
  category?: string
}

export const updateTournamentSettings = (parentId: string, data: { tc_name?: string; children?: TournamentSettingsChild[] }) =>
  api.put(`/competitions/parents/${parentId}/settings`, data)

export interface CreateTournamentData {
  tc_name: string
  children: TournamentSettingsChild[]
  create_edition?: boolean
  edition_year?: number
}

export interface CreateTournamentResponse {
  message: string
  tc_competition_parent_id: string
  tc_name: string
  children_created: Array<{ tc_competition_id: string; tc_name: string }>
  seasons_created: Array<{ tc_season_id: string; tc_competition_id: string; tc_name: string }>
  edition_year: number | null
  timestamp: string
}

export interface SeasonBackgroundImage {
  tc_image_id: string
  cdn_url: string
  thumbnail_url?: string
  pixel_width?: number
  pixel_height?: number
  aspect_ratio?: string
  mime_type?: string
  created_at?: string
}

export const createTournament = (data: CreateTournamentData): Promise<CreateTournamentResponse> =>
  api.post('/competitions/parents', data)

export const updateSeason = (seasonId: string, data: {
  tc_name?: string
  tc_year?: number
  tc_start_date?: string | null
  tc_end_date?: string | null
  tc_complex_id?: string | null
}) =>
  api.put(`/seasons/${seasonId}`, data)

export const createSeasonsBulk = (data: {
  parent_id: string
  year: number
  seasons: Array<{
    tc_competition_id: string
    tc_start_date?: string
    tc_end_date?: string
    tc_name?: string
  }>
}) =>
  api.post('/seasons/bulk', data)

export const deleteSeason = (seasonId: string) =>
  api.delete(`/seasons/${seasonId}`)

export const uploadSeasonBackground = (
  seasonId: string,
  file: File
): Promise<SeasonBackgroundImage> => {
  const formData = new FormData()
  formData.append('file', file)
  return api.upload<SeasonBackgroundImage>(`/seasons/${seasonId}/background`, formData)
}

export const deleteSeasonBackground = (seasonId: string): Promise<void> =>
  api.delete(`/seasons/${seasonId}/background`)

export const updateSeasonLicense = (seasonId: string, isLicensed: boolean) =>
  api.patch(`/seasons/${seasonId}/license`, { is_licensed: isLicensed })

export const bulkUpdateSeasonLicense = (data: {
  is_licensed: boolean
  competition_id?: string
  season_ids?: string[]
}) =>
  api.patch('/seasons/bulk-license', data)

export const deleteChildCompetition = (competitionId: string) =>
  api.delete(`/competitions/children/${competitionId}`)

// =============================================================================
// Complexes & Venues
// =============================================================================

export const createComplex = (data: { tc_name: string }) =>
  api.post('/complexes', data)

export const updateComplex = (complexId: string, data: { tc_name?: string }) =>
  api.put(`/complexes/${complexId}`, data)

export const createVenue = (data: {
  tc_complex_id: string
  tc_name: string
  tc_city?: string
  tc_country_code?: string
  tc_timezone?: string
}) =>
  api.post('/venues', data)

export const updateVenue = (venueId: string, data: {
  tc_name?: string
  tc_city?: string
  tc_country_code?: string
  tc_timezone?: string
}) =>
  api.put(`/venues/${venueId}`, data)

export const deleteVenue = (venueId: string) =>
  api.delete(`/venues/${venueId}`)

// =============================================================================
// Match VOD Count
// =============================================================================

export interface VodTypeInfo {
  tc_vod_asset_id: string
  publish_status: string
}

export interface MatchVodCountResponse {
  count: number
  status_counts: Record<string, number>
  tc_match_id: string
  vods: {
    highlight?: VodTypeInfo
    condensed?: VodTypeInfo
  }
}

export const fetchMatchVodCount = (matchId: string): Promise<MatchVodCountResponse> =>
  api.get(`/matches/${matchId}/vod-count`)

export interface LinkShoulderVodsResponse {
  match_id: string
  created_virtual_containers: Array<{ vod_type_code: string; tc_vod_asset_id: string }>
  linked_wsc_assets: Array<{ tc_vod_asset_id: string; vod_type_code: string; tc_title: string }>
  updated_wsc_assets: Array<{ tc_vod_asset_id: string; vod_type_code: string }>
}

export const linkShoulderVods = (matchId: string): Promise<LinkShoulderVodsResponse> =>
  api.post(`/matches/${matchId}/workflow/link-shoulder-vods`)

// =============================================================================
// Live Control
// =============================================================================

export interface LiveControlParams {
  status_filter?: string
  hours_upcoming?: number
  hours_recent?: number
  search?: string
  player_ids?: string
  categories?: string
  sort_order?: 'asc' | 'desc'
  page?: number
  limit?: number
}

export const fetchLiveControlMatches = (params?: LiveControlParams) =>
  api.get('/live-control', params)

// =============================================================================
// Match Documents
// =============================================================================

export const uploadMatchBriefing = (file: File) => {
  const formData = new FormData()
  formData.append('file', file)
  return api.upload<{
    message: string
    briefing_id: string
    s3_key: string
    file_name: string
    file_size: number
    uploaded_by: string
    uploaded_at: string
  }>('/match-briefing/upload', formData)
}

export interface MatchBriefing {
  id: string
  tournament_title: string
  tournament_dates: string | null
  match_note_date?: string | null
  tournament_day: string | null
  level: string | null
  prize_money: string | null
  html_s3_key: string | null
  html_url?: string | null
  pdf_s3_key?: string
  original_filename: string | null
  uploaded_by: string | null
  created_at: string | null
  updated_at: string | null
}

export interface MatchBriefingsResponse {
  briefings: MatchBriefing[]
  pagination: {
    page: number
    limit: number
    total_count: number
    total_pages: number
    has_next: boolean
    has_prev: boolean
  }
  filters: { days: number }
}

export const fetchMatchBriefings = (params?: { days?: number; page?: number; limit?: number }) =>
  api.get<MatchBriefingsResponse>('/match-briefing/briefings', params)

export const fetchMatchBriefing = (id: string) =>
  api.get<MatchBriefing>(`/match-briefing/briefings/${id}`)
